import React, { useState, useCallback, useEffect } from 'react';
import {
    Box, Typography, Button, Tabs, Tab, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Chip, IconButton,
    Tooltip, CircularProgress, Dialog, DialogTitle, DialogContent,
    DialogActions, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faCopy, faTrash, faPencil, faIdCard, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import CreateFormDialog from './CreateFormDialog';
import ApplicationsTable from './ApplicationsTable';
import baseURL from '../../../user/config';

const FORM_TYPE_LABELS = {
    membership: { label: 'Membership', icon: faIdCard, color: '#2D4A3D', bg: '#e8f5e9', categoryCol: 'Tiers', membersTab: 'Members' },
    event:      { label: 'Event',      icon: faCalendarAlt, color: '#1565c0', bg: '#e3f2fd', categoryCol: 'Tickets', membersTab: 'Attendees' },
};

const ORG_COLORS = {
    primary: '#2D4A3D',
    primaryLight: '#EBF4FD',
    text: '#1a1a2e',
    textSecondary: '#8a8f9d',
    border: '#eaecf0',
    background: '#FFFFFF',
    backgroundLight: '#F8F9FB',
};

const MembershipsManagement = ({ forms, refetchForms }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editForm, setEditForm] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [formToDelete, setFormToDelete] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    // Applications state
    const [applications, setApplications] = useState([]);
    const [applicationsTotal, setApplicationsTotal] = useState(0);
    const [applicationsLoading, setApplicationsLoading] = useState(false);
    const [selectedFormId, setSelectedFormId] = useState('');
    const [appPage, setAppPage] = useState(0);
    const [appRowsPerPage, setAppRowsPerPage] = useState(10);

    const fetchApplications = useCallback(async (formId, page, limit) => {
        setApplicationsLoading(true);
        try {
            const params = new URLSearchParams({ page: page + 1, limit });
            if (formId) params.append('formId', formId);
            const res = await fetch(`${baseURL}/org/membership-applications?${params}`, {
                credentials: 'include',
            });
            if (!res.ok) throw new Error('Failed to fetch applications.');
            const data = await res.json();
            setApplications(data.applications);
            setApplicationsTotal(data.total);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setApplicationsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 1) {
            fetchApplications(selectedFormId, appPage, appRowsPerPage);
        }
    }, [activeTab, selectedFormId, appPage, appRowsPerPage, fetchApplications]);

    const handleCopyLink = (slug, formType) => {
        const url = `${window.location.origin}/membership/${slug}`;
        navigator.clipboard.writeText(url).then(() => {
            const label = FORM_TYPE_LABELS[formType]?.label || 'Form';
            toast.success(`${label} link copied to clipboard!`);
        }).catch(() => {
            toast.error('Failed to copy link.');
        });
    };

    const handleDeleteClick = (form) => {
        setFormToDelete(form);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!formToDelete) return;
        setDeletingId(formToDelete.id);
        try {
            const res = await fetch(`${baseURL}/org/membership-forms/${formToDelete.id}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to delete form.');
            const ftLabel = FORM_TYPE_LABELS[formToDelete?.form_type]?.label || 'Membership';
            toast.success(`${ftLabel} form deleted.`);
            refetchForms();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setDeletingId(null);
            setDeleteConfirmOpen(false);
            setFormToDelete(null);
        }
    };

    const handleEditClick = async (form) => {
        try {
            const res = await fetch(`${baseURL}/org/membership-forms/${form.id}`, {
                credentials: 'include',
            });
            if (!res.ok) throw new Error('Failed to load form details.');
            const data = await res.json();
            setEditForm(data.form);
            setCreateDialogOpen(true);
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleDialogClose = () => {
        setCreateDialogOpen(false);
        setEditForm(null);
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                        width: 40, height: 40, borderRadius: '10px',
                        backgroundColor: `${ORG_COLORS.primary}15`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <FontAwesomeIcon icon={faIdCard} style={{ color: ORG_COLORS.primary, fontSize: '16px' }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: ORG_COLORS.text, fontSize: '1.1rem' }}>
                            Memberships &amp; Events
                        </Typography>
                        <Typography variant="body2" sx={{ color: ORG_COLORS.textSecondary, fontSize: '0.8rem' }}>
                            Create forms for memberships and events, manage applications
                        </Typography>
                    </Box>
                </Box>
                {activeTab === 0 && (
                    <Button
                        variant="contained"
                        startIcon={<FontAwesomeIcon icon={faPlus} />}
                        onClick={() => setCreateDialogOpen(true)}
                        sx={{
                            backgroundColor: ORG_COLORS.primary,
                            '&:hover': { backgroundColor: '#1e3329' },
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                        }}
                    >
                        Create Form
                    </Button>
                )}
            </Box>

            {/* Tabs */}
            <Box sx={{ borderBottom: `1px solid ${ORG_COLORS.border}`, mb: 2 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    sx={{
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', color: ORG_COLORS.textSecondary },
                        '& .Mui-selected': { color: ORG_COLORS.primary },
                        '& .MuiTabs-indicator': { backgroundColor: ORG_COLORS.primary },
                    }}
                >
                    <Tab label="Forms" />
                    <Tab label="Members &amp; Attendees" />
                </Tabs>
            </Box>

            {/* Forms Tab */}
            {activeTab === 0 && (
                <TableContainer component={Paper} sx={{ borderRadius: '12px', border: `1px solid ${ORG_COLORS.border}`, boxShadow: 'none' }}>
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead sx={{ bgcolor: ORG_COLORS.backgroundLight }}>
                            <TableRow>
                                {['Type', 'Form Title', 'Public Link', 'Status', 'Tiers / Tickets', 'Created', 'Actions'].map(h => (
                                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.8rem', color: ORG_COLORS.textSecondary }}>
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {forms.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: ORG_COLORS.textSecondary, fontSize: '0.875rem' }}>
                                        No forms yet. Create your first membership or event form to get started.
                                    </TableCell>
                                </TableRow>
                            )}
                            {forms.map((form) => {
                                const ftl = FORM_TYPE_LABELS[form.form_type] || FORM_TYPE_LABELS.membership;
                                return (
                                <TableRow key={form.id} sx={{ '&:hover': { bgcolor: ORG_COLORS.backgroundLight } }}>
                                    <TableCell>
                                        <Chip
                                            icon={<FontAwesomeIcon icon={ftl.icon} style={{ fontSize: '11px', marginLeft: '8px' }} />}
                                            label={ftl.label}
                                            size="small"
                                            sx={{ backgroundColor: ftl.bg, color: ftl.color, fontWeight: 600, fontSize: '0.72rem' }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.85rem', fontWeight: 600, color: ORG_COLORS.text }}>
                                        {form.title}
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography sx={{ fontSize: '0.75rem', color: ORG_COLORS.textSecondary, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                /membership/{form.slug}
                                            </Typography>
                                            <Tooltip title="Copy link">
                                                <IconButton size="small" onClick={() => handleCopyLink(form.slug, form.form_type)}>
                                                    <FontAwesomeIcon icon={faCopy} style={{ fontSize: '12px', color: ORG_COLORS.textSecondary }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={form.status === 'active' ? 'Active' : 'Inactive'}
                                            size="small"
                                            sx={{
                                                backgroundColor: form.status === 'active' ? '#e8f5e9' : '#fafafa',
                                                color: form.status === 'active' ? '#2e7d32' : ORG_COLORS.textSecondary,
                                                fontWeight: 600, fontSize: '0.72rem',
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.85rem', color: ORG_COLORS.text }}>
                                        {form.category_count || 0}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem', color: ORG_COLORS.textSecondary }}>
                                        {new Date(form.created_at).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <Tooltip title="Edit form">
                                                <IconButton size="small" onClick={() => handleEditClick(form)}>
                                                    <FontAwesomeIcon icon={faPencil} style={{ fontSize: '13px', color: ORG_COLORS.primary }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete form">
                                                <IconButton size="small" onClick={() => handleDeleteClick(form)} disabled={deletingId === form.id}>
                                                    {deletingId === form.id
                                                        ? <CircularProgress size={13} />
                                                        : <FontAwesomeIcon icon={faTrash} style={{ fontSize: '13px', color: '#ef5350' }} />}
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Members Tab */}
            {activeTab === 1 && (
                <Box>
                    <Box sx={{ mb: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 240 }}>
                            <InputLabel sx={{ fontSize: '0.85rem' }}>Filter by form</InputLabel>
                            <Select
                                value={selectedFormId}
                                onChange={(e) => { setSelectedFormId(e.target.value); setAppPage(0); }}
                                label="Filter by form"
                                sx={{ fontSize: '0.85rem', borderRadius: '8px' }}
                            >
                                <MenuItem value="">All forms</MenuItem>
                                {forms.map(f => (
                                    <MenuItem key={f.id} value={f.id}>{f.title}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <ApplicationsTable
                        applications={applications}
                        total={applicationsTotal}
                        page={appPage}
                        rowsPerPage={appRowsPerPage}
                        loading={applicationsLoading}
                        onPageChange={(_, newPage) => setAppPage(newPage)}
                        onRowsPerPageChange={(e) => { setAppRowsPerPage(parseInt(e.target.value, 10)); setAppPage(0); }}
                    />
                </Box>
            )}

            {/* Create/Edit Dialog */}
            <CreateFormDialog
                open={createDialogOpen}
                onClose={handleDialogClose}
                editData={editForm}
                onSuccess={refetchForms}
            />

            {/* Delete Confirmation */}
            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem' }}>
                    Delete {FORM_TYPE_LABELS[formToDelete?.form_type]?.label || 'Membership'} Form
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: '0.875rem', color: ORG_COLORS.textSecondary }}>
                        Are you sure you want to delete <strong>{formToDelete?.title}</strong>? This will remove all {FORM_TYPE_LABELS[formToDelete?.form_type]?.categoryCol.toLowerCase() || 'tiers'}, fields, and applications associated with it. This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)} sx={{ textTransform: 'none', color: ORG_COLORS.textSecondary }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        disabled={!!deletingId}
                        sx={{ textTransform: 'none', backgroundColor: '#ef5350', '&:hover': { backgroundColor: '#c62828' }, borderRadius: '8px' }}
                    >
                        {deletingId ? <CircularProgress size={16} sx={{ color: 'white' }} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MembershipsManagement;
